import React, { useEffect, useState, useRef, useCallback } from "react";
import { FiExternalLink } from "react-icons/fi"; // Importando o ícone
import "./DashboardPage.css";
import { relative } from "path";
import { useAuth } from "../../modules/auth";
import { FiltroGlobal, useFiltrosAtivos } from "../../../components/FiltrosGlobais";
import {Link} from 'react-router-dom'
const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface HomePageData {
  Gateways: number;
  Ativos: number;
  Locais: number;
  MqttUltimoPayload: string | null;
  AlertasGateway: number;
  AlertasLocal: number;
}

// Interface para o cache
interface CacheItem {
  data: HomePageData;
  timestamp: number;
  organizacoes: string[];
}

// Resposta do endpoint /organizacoes/resumo-locais (apenas VWCO)
interface ResumoPerimetroItem {
  Perimetro: string;
  QuantidadeAtivos: number;
}
interface ResumoLocalItem {
  Organizacao: string;
  Local: string;
  Perimetros: ResumoPerimetroItem[];
}

// Resposta do endpoint /corporacoes/resumo-associacao (apenas VWCO)
interface ResumoAssociacaoItem {
  IdOrganizacao: number;
  QtdAssociados: number;
  QtdNaoAssociados: number;
}

const DashboardPage = () => {
  const { auth } = useAuth();
  const user = auth?.User;
  const emailUsuario = user?.Email;

  // ✅ NOVO - Hook para acessar filtros globais
  const {
    selectedOrganizations,
    isInitialized,
    selectedCorporation,
    availableCorporations,
    availableOrganizations
  } = useFiltrosAtivos();

  useEffect(() => {
    console.log('VERSAO FRONT', '2026-01-08-1');
  }, []);

  const [dashboardData, setDashboardData] = useState<HomePageData>({
    Gateways: 0,
    Ativos: 0,
    Locais: 0,
    MqttUltimoPayload: null,
    AlertasGateway: 0,
    AlertasLocal: 0
  });

  const [mqttLoading, setMqttLoading] = useState<boolean>(false);

  const [isLoadingData, setIsLoadingData] = useState<boolean>(false);
  const [backgroundUrl, setBackgroundUrl] = useState<string | null>(null);
  const [corporationName, setCorporationName] = useState<string | null>(null);

  // Dados dos cards VWCO: resumo por local e resumo de associação
  const [resumoLocais, setResumoLocais] = useState<ResumoLocalItem[]>([]);
  const [resumoAssociacao, setResumoAssociacao] = useState<ResumoAssociacaoItem[]>([]);
  const [loadingResumoLocais, setLoadingResumoLocais] = useState<boolean>(false);
  const [loadingResumoAssociacao, setLoadingResumoAssociacao] = useState<boolean>(false);

  const cacheRef = useRef<Map<string, CacheItem>>(new Map());
  const CACHE_TTL = 30000;

  const forceUpdateRef = useRef<number>(0);

  const isVwco = corporationName === "VWCO";

  const removeQuotesFromMqtt = (value: string | null): string => {
    if (!value) return "N/A";
    return value.replace(/^["']|["']$/g, '');
  };

  const getCacheKey = (organizacoes: string[]) => {
    return organizacoes.sort().join('|');
  };

  const isCacheValid = (cacheItem: CacheItem) => {
    return Date.now() - cacheItem.timestamp < CACHE_TTL;
  };

  // ❌ REMOVIDO - Funções de buscar userId e organizações agora são automáticas no provider global

  // Função para buscar dados do dashboard (separada em duas etapas) com cache
  const fetchDashboardDataMulti = useCallback(async (organizacoes: string[], forceRefresh: boolean = false) => {
    if (!organizacoes || organizacoes.length === 0) {
      console.log('Nenhuma organização fornecida para buscar dados');
      return;
    }

    const cacheKey = getCacheKey(organizacoes);
    console.log('Buscando dados para organizações:', organizacoes, 'forceRefresh:', forceRefresh, 'cacheKey:', cacheKey);

    // Verificar cache se não for refresh forçado
    if (!forceRefresh) {
      const cachedItem = cacheRef.current.get(cacheKey);
      if (cachedItem && isCacheValid(cachedItem)) {
        console.log('💾 Usando dados do cache para organizações:', organizacoes);
        setDashboardData(cachedItem.data);
        setIsLoadingData(false);

        // Se os dados em cache não têm MQTT, carrega MQTT separadamente
        if (cachedItem.data.MqttUltimoPayload === null) {
          console.log('🔄 Cache sem MQTT, carregando MQTT separadamente...');
          setMqttLoading(true);

          // Função auxiliar para MQTT do cache com retry
          const fetchMqttFromCacheWithRetry = async (maxRetries: number = 3): Promise<any> => {
            for (let attempt = 1; attempt <= maxRetries; attempt++) {
              try {
                console.log(`🔄 Tentativa ${attempt}/${maxRetries} para carregar MQTT do cache`);
                const url = `${backendUrl}/api/HomePage/MQTT`;
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 15000);

                const response = await fetch(url, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify(organizacoes),
                  signal: controller.signal
                });

                clearTimeout(timeoutId);

                if (response.ok) {
                  const text = await response.text();
                  const value = isNaN(Number(text)) ? text : Number(text);
                  console.log(`✅ MQTT carregado na tentativa ${attempt}:`, value);
                  return value;
                }

                console.warn(`⚠️ Tentativa ${attempt} falhou com status:`, response.status);
                if (attempt === maxRetries) return null;

              } catch (error: any) {
                console.error(`❌ Erro na tentativa ${attempt} para MQTT do cache:`, error.name, error.message);

                // Se é a última tentativa, retorna null
                if (attempt === maxRetries) {
                  console.error(`💥 Todas as ${maxRetries} tentativas falharam para MQTT`);
                  return null;
                }

                // Aguarda um pouco antes da próxima tentativa (progressivo: 1s, 2s, 3s)
                await new Promise(resolve => setTimeout(resolve, attempt * 1000));
              }
            }
            return null;
          };

          fetchMqttFromCacheWithRetry(3)
            .then((MqttUltimoPayload: any) => {
              if (MqttUltimoPayload !== null) {
                const updatedData: HomePageData = {
                  ...cachedItem.data,
                  MqttUltimoPayload: (MqttUltimoPayload === null || typeof MqttUltimoPayload === 'string')
                    ? MqttUltimoPayload
                    : String(MqttUltimoPayload)
                };
                setDashboardData(updatedData);
                // Atualiza o cache com MQTT
                cacheRef.current.set(cacheKey, {
                  data: updatedData,
                  timestamp: Date.now(),
                  organizacoes: [...organizacoes]
                });
                console.log('✅ Cache atualizado com MQTT após retry');
              } else {
                console.log('❌ MQTT falhou após todas as tentativas - mantendo dados sem MQTT');
              }
            })
            .finally(() => setMqttLoading(false));
        }
        return;
      }
    }

    console.log('Fazendo chamada da API para organizações:', organizacoes);

    setIsLoadingData(true);

    try {
      // Função auxiliar para chamada com timeout
      const fetchValue = async (endpoint: string) => {
        const url = `${backendUrl}${endpoint}`;
        console.log('Fazendo request para:', url, 'com body:', organizacoes);

        try {
          // Promise com timeout de 15 segundos
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 15000);

          const response = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(organizacoes),
            signal: controller.signal
          });

          clearTimeout(timeoutId);

          if (response.ok) {
            // Pode ser texto ou número
            const text = await response.text();
            // Tenta converter para número, se não for, retorna texto
            const value = isNaN(Number(text)) ? text : Number(text);
            console.log(`✅ Resposta de ${endpoint}:`, value);
            return value;
          } else {
            console.error(`❌ Erro na requisição ${endpoint}:`, response.status, response.statusText);
            return null;
          }
        } catch (error: any) {
          if (error.name === 'AbortError') {
            console.error(`⏰ Timeout na requisição ${endpoint} (15s)`);
          } else {
            console.error(`💥 Erro na requisição ${endpoint}:`, error);
          }
          return null;
        }
      };

      // PRIMEIRA ETAPA: Carrega dados rápidos primeiro (sem MQTT)
      const [Gateways, Ativos, Locais, AlertasGateway, AlertasLocal] = await Promise.all([
        fetchValue('/api/HomePage/Gateways'),
        fetchValue('/api/HomePage/Ativos'),
        fetchValue('/api/HomePage/Locais'),
        fetchValue('/api/HomePage/GatewaysAlertas'),
        fetchValue('/api/HomePage/AtivosAlertas'),
      ]);

      // Cria objeto dos dados rápidos
      const rapidData: HomePageData = {
        Gateways: typeof Gateways === 'number' ? Gateways : Number(Gateways) || 0,
        Ativos: typeof Ativos === 'number' ? Ativos : Number(Ativos) || 0,
        Locais: typeof Locais === 'number' ? Locais : Number(Locais) || 0,
        AlertasGateway: typeof AlertasGateway === 'number' ? AlertasGateway : Number(AlertasGateway) || 0,
        AlertasLocal: typeof AlertasLocal === 'number' ? AlertasLocal : Number(AlertasLocal) || 0,
        MqttUltimoPayload: null // Será carregado separadamente
      };

      console.log('✅ Dados rápidos carregados:', rapidData);
      // Atualiza o estado com os dados rápidos IMEDIATAMENTE
      setDashboardData(rapidData);

      // FINALIZA o loading geral aqui - dados rápidos já estão prontos
      setIsLoadingData(false);
      console.log('🏁 Dados rápidos finalizados para organizações:', organizacoes);

      // Salva os dados rápidos no cache (sem MQTT)
      cacheRef.current.set(cacheKey, {
        data: rapidData,
        timestamp: Date.now(),
        organizacoes: [...organizacoes]
      });

      // SEGUNDA ETAPA: Carrega MQTT de forma COMPLETAMENTE INDEPENDENTE com retry
      setMqttLoading(true);
      console.log('🔄 Iniciando carregamento do MQTT separadamente...');

      // Função de retry para MQTT principal
      const fetchMqttWithRetry = async (maxRetries: number = 3): Promise<any> => {
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            console.log(`🔄 Tentativa ${attempt}/${maxRetries} para carregar MQTT principal`);
            const result = await fetchValue('/api/HomePage/MQTT');
            if (result !== null) {
              console.log(`✅ MQTT principal carregado na tentativa ${attempt}:`, result);
              return result;
            }
            console.warn(`⚠️ Tentativa ${attempt} retornou null`);
            if (attempt === maxRetries) return null;
          } catch (error: any) {
            console.error(`❌ Erro na tentativa ${attempt} para MQTT principal:`, error);
            if (attempt === maxRetries) {
              console.error(`💥 Todas as ${maxRetries} tentativas falharam para MQTT principal`);
              return null;
            }
            // Aguarda antes da próxima tentativa
            await new Promise(resolve => setTimeout(resolve, attempt * 1000));
          }
        }
        return null;
      };

      // Executa o retry em paralelo
      fetchMqttWithRetry(3)
        .then((MqttUltimoPayload) => {
          if (MqttUltimoPayload !== null) {
            const finalData: HomePageData = {
              ...rapidData,
              MqttUltimoPayload: (MqttUltimoPayload === null || typeof MqttUltimoPayload === 'string')
                ? MqttUltimoPayload
                : String(MqttUltimoPayload)
            };

            console.log('✅ MQTT principal finalizado:', MqttUltimoPayload);
            // Atualiza apenas o MQTT
            setDashboardData(finalData);

            // Atualiza o cache com dados completos
            cacheRef.current.set(cacheKey, {
              data: finalData,
              timestamp: Date.now(),
              organizacoes: [...organizacoes]
            });
          } else {
            console.log('❌ MQTT principal falhou após todas as tentativas - mantendo dados sem MQTT');
          }
        })
        .finally(() => {
          setMqttLoading(false);
          console.log('🏁 MQTT principal finalizado (com ou sem sucesso)');
        });

    } catch (error) {
      console.error('💥 Erro geral ao buscar dados rápidos:', error);
      const errorData: HomePageData = {
        Gateways: 0,
        Ativos: 0,
        Locais: 0,
        MqttUltimoPayload: null,
        AlertasGateway: 0,
        AlertasLocal: 0
      };
      setDashboardData(errorData);
      setMqttLoading(false);
      setIsLoadingData(false);
      console.log('❌ Erro ao carregar dados rápidos para organizações:', organizacoes);
    }
  }, []);

  // ✅ NOVO - useEffect para monitorar mudanças nos filtros globais
  useEffect(() => {
    console.log('useEffect disparado - selectedOrganizations:', selectedOrganizations, 'isInitialized:', isInitialized);

    if (isInitialized && selectedOrganizations.length > 0) {
      console.log('Vai buscar dados para organizações:', selectedOrganizations);
      fetchDashboardDataMulti(selectedOrganizations);
    } else if (isInitialized) {
      console.log('Resetando dados - nenhuma organização selecionada ou não inicializado');
      // Reseta os dados quando não há organizações selecionadas
      setDashboardData({
        Gateways: 0,
        Ativos: 0,
        Locais: 0,
        MqttUltimoPayload: null,
        AlertasGateway: 0,
        AlertasLocal: 0
      });
    }
  }, [selectedOrganizations, isInitialized, forceUpdateRef.current, fetchDashboardDataMulti]);


  useEffect(() => {
    if (isInitialized) {
      if (selectedCorporation && availableCorporations.length > 0) {
        const foundCorp = availableCorporations.find(corp => corp.id === selectedCorporation);

        if (foundCorp) {
          console.log(`Corporação selecionada: ${foundCorp.name}`);
          setCorporationName(foundCorp.name);
        } else {
          console.warn(`Corporação com ID ${selectedCorporation} não encontrada.`);
          setCorporationName(null);
        }
      } else {
        setCorporationName(null);
      }
    }
  }, [isInitialized, selectedCorporation, availableCorporations]);

  // Busca resumo de locais e resumo de associação apenas quando VWCO e há organizações selecionadas
  useEffect(() => {
    if (!isVwco || !selectedOrganizations.length || !availableOrganizations?.length || !backendUrl) {
      if (!isVwco) {
        setResumoLocais([]);
        setResumoAssociacao([]);
      }
      return;
    }

    const idOrganizacoes = selectedOrganizations
      .map((name) => availableOrganizations.find((o) => o.OrganizationName === name)?.OrganizationId)
      .filter((id): id is number => id != null);

    if (idOrganizacoes.length === 0) {
      setResumoLocais([]);
      setResumoAssociacao([]);
      return;
    }

    const body = { IdOrganizacoes: idOrganizacoes };

    const fetchResumoLocais = async () => {
      setLoadingResumoLocais(true);
      try {
        const res = await fetch(`${backendUrl}/organizacoes/resumo-locais`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (res.ok) {
          const data: ResumoLocalItem[] = await res.json();
          setResumoLocais(Array.isArray(data) ? data : []);
        } else {
          setResumoLocais([]);
        }
      } catch (e) {
        console.error("Erro ao buscar resumo de locais:", e);
        setResumoLocais([]);
      } finally {
        setLoadingResumoLocais(false);
      }
    };

    const fetchResumoAssociacao = async () => {
      setLoadingResumoAssociacao(true);
      try {
        const res = await fetch(`${backendUrl}/corporacoes/resumo-associacao`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        if (res.ok) {
          const data: ResumoAssociacaoItem[] = await res.json();
          setResumoAssociacao(Array.isArray(data) ? data : []);
        } else {
          setResumoAssociacao([]);
        }
      } catch (e) {
        console.error("Erro ao buscar resumo de associação:", e);
        setResumoAssociacao([]);
      } finally {
        setLoadingResumoAssociacao(false);
      }
    };

    fetchResumoLocais();
    fetchResumoAssociacao();
  }, [isVwco, selectedOrganizations, availableOrganizations, backendUrl]);

  useEffect(() => {
    // Função para buscar a URL da imagem de background
    const fetchBackgroundUrl = async (corpName: string) => {
      if (!corpName || !backendUrl) return;

      console.log(`Buscando URL de background para corporação: ${corpName}`);
      try {
        const url = `${backendUrl}/api/arquivos/corporacao/${encodeURIComponent(corpName)}/background-url`;
        const response = await fetch(url);

        if (response.ok) {
          const data = await response.json();
          setBackgroundUrl(data.url); // Armazena a URL no estado
          console.log(`URL de background carregada: ${data.url}`);
        } else {
          console.warn(`Imagem de background não encontrada para ${corpName} ou falha na API.`);
          setBackgroundUrl(null); // Limpa se não encontrar
        }
      } catch (error) {
        console.error('Erro ao buscar a URL do background:', error);
        setBackgroundUrl(null); // Limpa em caso de erro
      }
    };
    const nomeCorpAtual = corporationName;

    if (nomeCorpAtual) {
      fetchBackgroundUrl(nomeCorpAtual);
    } else {
      setBackgroundUrl(null);
    }

  }, [corporationName, backendUrl]);

  useEffect(() => {
    // Encontra os elementos do layout do Metronic
    const mainElement = document.getElementById('kt_app_main'); // O container principal
    const toolbarElement = document.getElementById('kt_app_toolbar'); // A toolbar
    const contentElement = document.getElementById('kt_app_content'); // O container do conteúdo

    // Também torna o 'kt_app_root' e 'kt_app_page' transparentes
    const rootElement = document.getElementById('kt_app_root');
    const pageElement = document.getElementById('kt_app_page');

    if (mainElement && toolbarElement && contentElement) {
      if (backgroundUrl) {
        // 1. Aplica a imagem ao container principal (kt_app_main)
        mainElement.style.backgroundImage = `url(${backgroundUrl})`;
        mainElement.style.backgroundSize = '100% 100%'; // Usando a sua última configuração para esticar
        mainElement.style.backgroundPosition = 'center center';
        mainElement.style.backgroundRepeat = 'no-repeat';
        mainElement.style.backgroundAttachment = 'fixed';
        mainElement.style.minHeight = '90vh';

        // 2. FORÇA os containers filhos (toolbar e content) a serem transparentes
        toolbarElement.style.backgroundColor = 'transparent';
        contentElement.style.backgroundColor = 'transparent';

        // Garante que os pais também sejam transparentes
        if (rootElement) rootElement.style.backgroundColor = 'transparent';
        if (pageElement) pageElement.style.backgroundColor = 'transparent';

      } else {
        // 3. Se não há URL, remove todos os estilos
        mainElement.style.backgroundImage = '';
        mainElement.style.minHeight = '';
        toolbarElement.style.backgroundColor = '';
        contentElement.style.backgroundColor = '';
        if (rootElement) rootElement.style.backgroundColor = '';
        if (pageElement) pageElement.style.backgroundColor = '';
      }
    }

    // 4. Função de limpeza: Roda quando o usuário sai da página
    return () => {
      if (mainElement) {
        mainElement.style.backgroundImage = '';
        mainElement.style.minHeight = '';
      }
      if (toolbarElement) {
        toolbarElement.style.backgroundColor = '';
      }
      if (contentElement) {
        contentElement.style.backgroundColor = '';
      }
      if (rootElement) {
        rootElement.style.backgroundColor = '';
      }
      if (pageElement) {
        pageElement.style.backgroundColor = '';
      }
    };
  }, [backgroundUrl]);

  // ✅ NOVO - Callback quando filtros mudam
  const handleFiltroChange = () => {
    console.log('🔄 Filtros mudaram, limpando cache...');
    // Limpar cache quando filtros mudam
    cacheRef.current.clear();
    forceUpdateRef.current += 1;
  };

  // Função para limpar cache quando necessário
  const clearCache = () => {
    console.log('🧹 Limpando cache e forçando atualização...');
    cacheRef.current.clear();
    forceUpdateRef.current += 1;

    // Se há organizações selecionadas, força a busca
    if (selectedOrganizations.length > 0) {
      console.log('🔄 Forçando busca para organizações:', selectedOrganizations);
      fetchDashboardDataMulti(selectedOrganizations, true);
    }
  };

  console.log('Render - selectedOrganizations:', selectedOrganizations, 'isInitialized:', isInitialized);

  // ✅ Estado de loading enquanto filtros inicializam
  if (!isInitialized) {
    return (
      <div
        className="dashboard-container"
      >
        <div style={{ padding: "20px", textAlign: "center" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
            <i className="ui loading spinner icon"></i>
            <span>Carregando filtros...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* ✅ NOVO - Filtros globais simplificados */}
      <div style={{ marginBottom: "20px", display: "flex", gap: "10px", alignItems: "flex-end" }}>
        <FiltroGlobal
          onCorporationChange={handleFiltroChange}
          onOrganizationChange={handleFiltroChange}
          style={{
            corporation: { width: "300px" },
            organization: { width: "300px" }
          }}
        />

        {/* Botão para forçar atualização */}
        <div>
          <button
            onClick={clearCache}
            style={{
              padding: "8px 16px",
              backgroundColor: "#007bff",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontSize: "14px"
            }}
            title="Forçar atualização dos dados"
          >
            🔄 Atualizar
          </button>
        </div>
      </div>
      <div className="top-squares">
        <div className="square">
          <div
            className="title-wrapper"
            style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
          >
            <h3>Gateways</h3>
            <Link
                  to={`/gateway-mapa`} 
                  className="redirect-link"
                  style={{ display: "inline-flex" }}
                >
                  <i
                    className="ui icon external alternate"
                    style={{ position: "relative", top: "-3px" }}
                  ></i>
                </Link>
          </div>
          <br />
          {isLoadingData ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
              <i className="ui loading spinner icon"></i>
              <span>Carregando...</span>
            </div>
          ) : (
            <p>{dashboardData.Gateways}</p>
          )}
        </div>
        <div className="square">
          <div
            className="title-wrapper"
            style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
          >
            {/* Se for VWCO, exibe "Beacon", senão "Ativos" */}
            <h3 style={{ margin: 0 }}>{isVwco ? "Beacons" : "Ativos"}</h3>
            <Link
                  to={`/ativos-mapa`} 
                  className="redirect-link"
                  style={{ display: "inline-flex" }}
                >
                  <i
                    className="ui icon external alternate"
                    style={{ position: "relative", top: "-3px" }}
                  ></i>
                </Link>
          </div>
          <br />
          {isLoadingData ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
              <i className="ui loading spinner icon"></i>
              <span>Carregando...</span>
            </div>
          ) : (
            <p>{dashboardData.Ativos}</p>
          )}
        </div>
        <div className="square">
          <div
            className="title-wrapper"
            style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
          >
            <h3>Locais</h3>
            <Link
                  to={`/maplocal`} 
                  className="redirect-link"
                  style={{ display: "inline-flex" }}
                >
                  <i
                    className="ui icon external alternate"
                    style={{ position: "relative", top: "-3px" }}
                  ></i>
                </Link>
          </div>
          <br />
          {isLoadingData ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
              <i className="ui loading spinner icon"></i>
              <span>Carregando...</span>
            </div>
          ) : (
            <p>{dashboardData.Locais}</p>
          )}
        </div>

        {/* 4º card na primeira linha: Resumo de associação (apenas VWCO) */}
        {isVwco && (
          <div className="square">
            <div
              className="title-wrapper"
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <h3 style={{ margin: 0 }}>Resumo de associação</h3>
              <i className="ui icon link" style={{ position: "relative", top: "-2px" }} aria-hidden></i>
            </div>
            <br />
            {loadingResumoAssociacao ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
                <i className="ui loading spinner icon"></i>
                <span>Carregando...</span>
              </div>
            ) : resumoAssociacao.length === 0 ? (
              <p style={{ fontSize: "1.2rem" }}>—</p>
            ) : (
              <div className="square-resumo-associacao">
                {resumoAssociacao.map((item, idx) => (
                  <React.Fragment key={`${item.IdOrganizacao}-${idx}`}>
                    <div className="square-resumo-line">
                      <span className="square-resumo-label">Associados</span>
                      <span className="square-resumo-num">{item.QtdAssociados}</span>
                    </div>
                    <div className="square-resumo-line">
                      <span className="square-resumo-label">Disponivel</span>
                      <span className={item.QtdNaoAssociados > 0 ? "square-resumo-num square-resumo-num-alert" : "square-resumo-num"}>
                        {item.QtdNaoAssociados}
                      </span>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Só renderiza o MQTT se NÃO for VWCO. Ao remover, os outros squares ocupam o espaço */}
        {!isVwco && (
          <div className="square">
            <div
              className="title-wrapper"
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <h3>MQTT (último payload)</h3>
              <a
                href={`/Mqtt${selectedOrganizations.length > 0 ? '?org=' + encodeURIComponent(selectedOrganizations.join(',')) : ''}`}
                className="redirect-link"
                style={{ display: "inline-flex" }}
              >
                <i
                  className="ui icon external alternate"
                  style={{ position: "relative", top: "-3px" }}
                ></i>
              </a>
            </div>
            <br />
            {mqttLoading ? (
              <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", fontSize: "1.8rem" }}>
                <i className="ui loading spinner icon"></i>
                <span>Carregando...</span>
              </div>
            ) : (
              <p style={{ fontSize: "1.8rem" }}>{removeQuotesFromMqtt(dashboardData.MqttUltimoPayload)}</p>
            )}
          </div>
        )}
      </div>

      {/* Alerts section */}
      <div className="alerts-section">
        {/* Quando VWCO: barra entre 1ª e 3ª linha com "Locais e ativos" + lista; senão: título "Alertas" */}
        <div className="alerts-header">
          {isVwco ? (
            <>
              <h1 className="vwco-locais-titulo">Locais e ativos</h1>
              <div className="vwco-locais-strip-body">
                {loadingResumoLocais ? (
                  <div className="vwco-loading" style={{ padding: "1rem 0" }}>
                    <i className="ui loading spinner icon"></i>
                    <span>Carregando...</span>
                  </div>
                ) : resumoLocais.length === 0 ? (
                  <p className="vwco-empty" style={{ marginTop: "0.5rem" }}>Nenhum local encontrado.</p>
                ) : (
                  <ul className="vwco-locais-list">
                    {resumoLocais.map((item, idx) => {
                      const totalAtivos = (item.Perimetros ?? []).reduce((s, p) => s + (p.QuantidadeAtivos ?? 0), 0);
                      return (
                        <li key={`${item.Organizacao}-${item.Local}-${idx}`}>
                          <span className="vwco-local-nome">{item.Local}</span>
                          <span className="vwco-local-qtd-badge">{totalAtivos}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </>
          ) : (
            <div
              className="title-wrapper"
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <h1>Alertas</h1>
            </div>
          )}
        </div>

        <div className="alert-squares">
          <div className="alert-square">
            <div className="alert-content">
              <div
                className="title-wrapper"
                style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
              >
                {/* Título condicional: "Alertas Gateway" se VWCO, senão "Gateway" */}
                <h2>{isVwco ? "Alertas Gateway" : "Gateway"}</h2>
               <Link
                  to={`/visao-geral?type=gateway`} 
                  className="redirect-link"
                  style={{ display: "inline-flex" }}
                >
                  <i
                    className="ui icon external alternate"
                    style={{ position: "relative", top: "-3px" }}
                  ></i>
                </Link>
              </div>
              {/* Oculta o subtítulo se for VWCO */}
              {!isVwco && <h4>Alertas abertos</h4>}
              <p className="alert-number">{dashboardData.AlertasGateway}</p>
            </div>
          </div>
          <div className="alert-square">
            <div className="alert-content">
              <div
                className="title-wrapper"
                style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
              >
                {/* Título condicional: "Alertas Beacon" se VWCO, senão "Ativo" */}
                <h2>{isVwco ? "Alertas Beacon" : "Ativo"}</h2>
                <Link
                  to={`/visao-geral?type=Ativo&situacao=0`} 
                  className="redirect-link"
                  style={{ display: "inline-flex" }}
                >
                  <i
                    className="ui icon external alternate"
                    style={{ position: "relative", top: "-3px" }}
                  ></i>
                </Link>
              </div>
              {/* Oculta o subtítulo se for VWCO */}
              {!isVwco && <h4>Alertas abertos</h4>}
              <p className="alert-number">{dashboardData.AlertasLocal}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export { DashboardPage };